"use client";
import { buildHref } from "@softmax/web-addresses";
import { AccountMenuView, type AccountAgent } from "@softmax/web-ux/shell";
import { useBrowserSession } from "../../session/use-browser-session";
import { useActualViewer } from "../../session/use-actual-viewer";
import { FeatureLink as Link } from "../../navigation/feature-link";
import { useTrackedSignOut } from "../../telemetry/analytics/use-tracked-sign-out";
import { analyticsEvents } from "../../telemetry/analytics/events";
import { analyticsSources } from "../../telemetry/analytics/sources";

export type { AccountAgent } from "@softmax/web-ux/shell";

/** The signed-in reader's settings page; also the account pill's profile door. */
export const accountSettingsHref = buildHref({ name: "account" });

const accountLinks = [
  { href: accountSettingsHref, label: "Account settings" },
  { href: buildHref({ name: "coworlds" }), label: "Manage Coworlds" },
];

export function AccountMenu({
  anchor,
  className,
  agent = null,
}: {
  readonly anchor: "masthead" | "rail";
  readonly className?: string;
  readonly agent?: AccountAgent | null;
}) {
  const { session } = useBrowserSession();
  const signOut = useTrackedSignOut({
    event: analyticsEvents.siteNavAccountMenuSignOutClicked,
    properties: { source: analyticsSources.siteNavAccountMenu },
  });
  const viewer = useActualViewer();
  const viewerId = viewer.data?.subject_id ?? null;
  const user = viewer.data
    ? {
        name: viewer.data.name ?? null,
        email: viewer.data.user_email,
        image:
          session?.user.id === viewerId ? (session.user.image ?? null) : null,
      }
    : null;

  // The caller owns signed-in visibility, so the avatar keeps its space while profile fields load.
  return (
    <div className={className}>
      <AccountMenuView
        anchor={anchor}
        user={user}
        agent={agent}
        links={accountLinks}
        renderLink={({ href, newTab }) => (
          <Link
            href={href}
            target={newTab ? "_blank" : undefined}
            rel={newTab ? "noopener noreferrer" : undefined}
          />
        )}
        onSignOut={() => signOut.mutate()}
        signOutState={
          signOut.isPending
            ? { status: "pending" }
            : signOut.error
              ? { status: "error", message: signOut.error.message }
              : { status: "idle" }
        }
      />
    </div>
  );
}
